/**
 * A divisa da ficha: o que fica dentro da caixa dourada e o que fica fora dela.
 *
 * <p><b>Quem decide é o backend.</b> A lista de campos do jogador vem de
 * `GET /mundo/in-game` — ver `ConteudoInGame.java`. Este arquivo não sabe o que é
 * in-game; ele sabe o que fazer com a resposta.
 *
 * <p>Morava no esquema do front, e ali servia só para desenhar. No dia em que a
 * exportação para a engine existir, o desenho e a exportação teriam que concordar sobre
 * a mesma lista — e duas listas que precisam concordar divergem, é só questão de quando.
 * É o mesmo motivo de `progressoGeral.ts` existir.
 */
import type { Campo, DefinicaoColecao } from './esquema';

/** Coleção → campos que o jogador vê. Coleção ausente quer dizer "nada daqui". */
export type ConteudoInGame = Record<string, string[]>;

export interface DivisaDaFicha {
  /** Se esta ficha tem caixa dourada. */
  temCaixa: boolean;
  /** Os campos de cabeçalho que vão dentro dela — ou todos, quando não há caixa. */
  dentroDaCaixa: Campo[];
  /** Os campos de cabeçalho que ficam na faixa de fora. Vazio quando não há caixa. */
  foraDaCaixa: Campo[];
}

/**
 * Reparte os campos de cabeçalho de uma ficha.
 *
 * <p><b>Sem campo do jogador não há caixa.</b> Não existe um marcador de coleção
 * separado do de campo, e isso não é economia: os dois juntos permitiam o estado
 * "coleção marcada, nenhum campo marcado", que na tela virava uma moldura dourada
 * dizendo "o que o jogador vê" em volta de nada. Quests estava exatamente assim.
 *
 * <p>Com {@code inGame} ainda não carregado o resultado é o mesmo de uma coleção sem
 * conteúdo do jogador — a ficha de sempre. Quem chama espera o carregamento antes de
 * desenhar, para a caixa não aparecer depois de um quadro sem ela.
 */
export function divisaDaFicha(
  definicao: DefinicaoColecao,
  inGame: ConteudoInGame | undefined
): DivisaDaFicha {
  const noTopo = definicao.campos.filter((c) => c.cabecalho);
  const doJogador = new Set(inGame?.[definicao.id] ?? []);
  const dentro = noTopo.filter((c) => doJogador.has(c.chave));

  if (dentro.length === 0) {
    return { temCaixa: false, dentroDaCaixa: noTopo, foraDaCaixa: [] };
  }

  return {
    temCaixa: true,
    dentroDaCaixa: dentro,
    foraDaCaixa: noTopo.filter((c) => !doJogador.has(c.chave)),
  };
}
